import * as React from 'react';
import { useMemo } from 'react';
import { Controller, UseFormReturn } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import SelectField from '../../../core/components/Fields/SelectField.tsx';
import Switch from '../../../core/components/Switch.tsx';
import Table from '../../../core/components/Table.tsx';
import {
    SmartInboxFormData,
    SmartInboxProductConfigType,
    SUPPORTED_PRODUCT_TYPES,
} from '../../../customer/pages/SmartInbox/types.ts';
import { DOCUMENT_TYPE } from '../../../document/constants.ts';

interface DestinationFolder {
    id: string;
    name: string;
}

interface EmailFilingFormFieldsProps {
    form: UseFormReturn<SmartInboxFormData>;
    destinationFolders: DestinationFolder[];
    productConfigs?: SmartInboxProductConfigType[];
    isLoading?: boolean;
}

const MARK_AS_READ_EVENTS = ['DOCUMENT_ROUTED', 'DOCUMENT_FINISHED', 'DOCUMENT_DELETED', 'DOCUMENT_DISCARDED'];

const FOLDER_COLUMNS = [
    { key: 'inProgress', eventKind: 'DOCUMENT_ROUTED' },
    { key: 'done', eventKind: 'DOCUMENT_FINISHED' },
    { key: 'deleted', eventKind: 'DOCUMENT_DELETED' },
];

const FolderCell = ({
    form,
    documentType,
    eventKind,
    folderOptions,
    isLoading,
}: {
    form: UseFormReturn<SmartInboxFormData>;
    documentType: string;
    eventKind: string;
    folderOptions: { value: string; label: string }[];
    isLoading?: boolean;
}) => {
    const { t } = useTranslation('integrations');
    const actionKey = `${eventKind}_MOVE_TO_FOLDER`;

    return (
        <Controller
            name={`productConfigs.${documentType}.actionConfigs.${actionKey}.actionConfig`}
            control={form.control}
            render={({ field }) => {
                const currentFolderId = Object.values(field.value || {})[0] as string;
                return (
                    <div onWheel={(e) => e.stopPropagation()}>
                        <SelectField
                            value={currentFolderId}
                            options={folderOptions}
                            renderOption={(option) => option.label}
                            placeholder={t('smartInbox.form.productConfig.actions.movingActionPlaceHolder')}
                            disabled={isLoading}
                            onValueChange={(folderId) => field.onChange({ [documentType]: folderId })}
                        />
                    </div>
                );
            }}
        />
    );
};

const MarkAsReadCell = ({
    form,
    documentType,
    isLoading,
}: {
    form: UseFormReturn<SmartInboxFormData>;
    documentType: string;
    isLoading?: boolean;
}) => {
    const checked = form.watch(
        `productConfigs.${documentType}.actionConfigs.${MARK_AS_READ_EVENTS[0]}_MARK_AS_READ.actionConfig`
    );

    const handleChange = (value: boolean) => {
        MARK_AS_READ_EVENTS.forEach((eventKind) => {
            form.setValue(
                `productConfigs.${documentType}.actionConfigs.${eventKind}_MARK_AS_READ.actionConfig`,
                value,
                {
                    shouldDirty: true,
                }
            );
        });
    };

    return <Switch value={!!checked} onValueChange={handleChange} disabled={isLoading} />;
};

const EmailFilingFormFields = ({ form, destinationFolders, productConfigs, isLoading }: EmailFilingFormFieldsProps) => {
    const { t } = useTranslation('mailboxes');

    const folderOptions = useMemo(
        () => destinationFolders.map((folder) => ({ value: folder.id, label: folder.name })),
        [destinationFolders]
    );

    const rows = useMemo(
        () =>
            (productConfigs || [])
                .map((config) => config.documentType as DOCUMENT_TYPE)
                .filter((type) => SUPPORTED_PRODUCT_TYPES.includes(type))
                .sort((a, b) => SUPPORTED_PRODUCT_TYPES.indexOf(a) - SUPPORTED_PRODUCT_TYPES.indexOf(b)),
        [productConfigs]
    );

    if (rows.length === 0) {
        return <div className="text-content-medium text-sm py-4">{t('detail.settingsTab.emailFiling.noChannels')}</div>;
    }

    return (
        <Table className="w-full border-none shadow-none -ml-3">
            <Table.Head>
                <Table.Row className="border-none">
                    <Table.HeadCell className="bg-transparent border-none">
                        {t('detail.settingsTab.emailFiling.category')}
                    </Table.HeadCell>
                    {FOLDER_COLUMNS.map((column) => (
                        <Table.HeadCell key={column.key} className="bg-transparent border-none">
                            {t(`detail.settingsTab.emailFiling.columns.${column.key}`)}
                        </Table.HeadCell>
                    ))}
                    <Table.HeadCell className="bg-transparent border-none">
                        {t('detail.settingsTab.emailFiling.markAsRead')}
                    </Table.HeadCell>
                </Table.Row>
            </Table.Head>
            <Table.Body>
                {rows.map((documentType) => (
                    <Table.Row key={documentType} className="border-none">
                        <Table.Cell className="font-medium bg-transparent border-none">
                            {t(`detail.settingsTab.emailFiling.documentTypes.${documentType.toLowerCase()}`)}
                        </Table.Cell>
                        {FOLDER_COLUMNS.map((column) => (
                            <Table.Cell key={column.key} className="bg-transparent border-none">
                                <FolderCell
                                    form={form}
                                    documentType={documentType}
                                    eventKind={column.eventKind}
                                    folderOptions={folderOptions}
                                    isLoading={isLoading}
                                />
                            </Table.Cell>
                        ))}
                        <Table.Cell className="bg-transparent border-none">
                            <MarkAsReadCell form={form} documentType={documentType} isLoading={isLoading} />
                        </Table.Cell>
                    </Table.Row>
                ))}
            </Table.Body>
        </Table>
    );
};

export default EmailFilingFormFields;
